<section class="d-flex flex-column gap-2" data-testid="settings-page">
  <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
    <div class="d-flex flex-column gap-1">
      <h2 class="av-section-title m-0">{{ 'backoffice.settings.title' | translate }}</h2>
      <p class="av-meta m-0">{{ 'backoffice.settings.subtitle' | translate }}</p>
    </div>

    <button type="button" class="av-btn" (click)="reload()" data-testid="settings-reload">
      <mat-icon aria-hidden="true">refresh</mat-icon>
      <span>{{ 'backoffice.common.reload' | translate }}</span>
    </button>
  </div>

  <p class="av-meta m-0" data-testid="settings-scope-note">
    {{ 'backoffice.settings.scope_note' | translate }}
  </p>

  <div class="av-banner p-3 d-flex align-items-start gap-2" data-testid="settings-read-only">
    <mat-icon aria-hidden="true">lock</mat-icon>
    <div class="d-flex flex-column">
      <span class="cell-strong">{{ 'backoffice.settings.read_only.title' | translate }}</span>
      <span>{{ 'backoffice.settings.read_only.message' | translate }}</span>
    </div>
  </div>
</section>

@switch (facade.state()) {
  @case ('error') {
    <av-empty-state
      class="mt-3"
      icon="error_outline"
      [title]="'backoffice.settings.error.title' | translate"
      [message]="'backoffice.settings.error.message' | translate"
    ></av-empty-state>
  }
  @case ('ready') {
    @if (facade.settings(); as settings) {
      <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="settings-alerts">
        <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <h3 class="av-section-title m-0">{{ 'backoffice.settings.alerts.title' | translate }}</h3>

          <span class="av-meta" data-testid="settings-store">
            {{
              'backoffice.settings.alerts.store_value'
                | translate
                  : {
                      label: 'backoffice.settings.alerts.store_label' | translate,
                      store: settings.store,
                    }
            }}
          </span>
        </div>

        @if (facade.unconfiguredCount() > 0) {
          <div
            class="av-banner p-3 d-flex align-items-start gap-2"
            data-testid="settings-no-recipient-warning"
          >
            <mat-icon aria-hidden="true">notifications_off</mat-icon>
            <span>
              {{
                'backoffice.settings.alerts.no_recipient_warning'
                  | translate
                    : { unconfigured: facade.unconfiguredCount(), total: facade.alerts().length }
              }}
            </span>
          </div>
        }

        <div class="table-scroll">
          <table class="av-table w-100" data-testid="alert-table">
            <thead>
              <tr>
                <th scope="col">
                  {{ 'backoffice.settings.alerts.columns.kind' | translate }}
                </th>
                <th scope="col">
                  {{ 'backoffice.settings.alerts.columns.trigger' | translate }}
                </th>
                <th scope="col">
                  {{ 'backoffice.settings.alerts.columns.recipients' | translate }}
                </th>
                <th scope="col">
                  {{ 'backoffice.settings.alerts.columns.configured' | translate }}
                </th>
              </tr>
            </thead>

            <tbody>
              @for (alert of settings.alerts; track alert.kind) {
                <tr>
                  <td class="cell-strong" data-testid="alert-kind">
                    {{ kindLabelKey(alert.kind) | translate }}
                  </td>
                  <td class="cell-muted">{{ kindDescriptionKey(alert.kind) | translate }}</td>
                  <td class="cell-muted">
                    {{
                      alert.recipients.length === 0
                        ? ('backoffice.settings.alerts.no_recipient' | translate)
                        : alert.recipients.join(', ')
                    }}
                  </td>
                  <td>
                    <av-status-chip
                      [label]="
                        (alert.configured ? 'backoffice.common.yes' : 'backoffice.common.no')
                          | translate
                      "
                      [tone]="alert.configured ? 'positive' : 'warning'"
                    ></av-status-chip>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <p class="av-meta m-0" data-testid="alert-kind-count">
          {{ 'backoffice.settings.alerts.count' | translate: { count: settings.alerts.length } }}
        </p>
      </section>

      <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="settings-thresholds">
        <h3 class="av-section-title m-0">
          {{ 'backoffice.settings.thresholds.title' | translate }}
        </h3>

        <div class="d-flex flex-wrap gap-4">
          <div class="d-flex flex-column gap-1">
            <span class="av-meta">
              {{ 'backoffice.settings.thresholds.queue_failed_red' | translate }}
            </span>
            <span class="cell-strong">{{ settings.thresholds.queue.failed_red }}</span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">
              {{ 'backoffice.settings.thresholds.queue_pending_amber' | translate }}
            </span>
            <span class="cell-strong">{{ settings.thresholds.queue.pending_amber }}</span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">
              {{ 'backoffice.settings.thresholds.webhooks_failed_red' | translate }}
            </span>
            <span class="cell-strong">{{ settings.thresholds.webhooks.failed_red }}</span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">
              {{ 'backoffice.settings.thresholds.webhooks_unprocessed_amber' | translate }}
            </span>
            <span class="cell-strong">{{ settings.thresholds.webhooks.unprocessed_amber }}</span>
          </div>

          <div class="d-flex flex-column gap-1">
            <span class="av-meta">
              {{ 'backoffice.settings.thresholds.oldest_red_minutes' | translate }}
            </span>
            <span class="cell-strong">{{ settings.thresholds.webhooks.oldest_red_minutes }}</span>
          </div>
        </div>
      </section>

      <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="settings-commands">
        <div class="d-flex flex-column gap-1">
          <h3 class="av-section-title m-0">
            {{ 'backoffice.settings.commands.title' | translate }}
          </h3>
          <p class="av-meta m-0">{{ 'backoffice.settings.commands.hint' | translate }}</p>
        </div>

        <ul class="m-0 ps-3" data-testid="tracked-command-list">
          @for (command of settings.tracked_commands; track command) {
            <li class="av-source">{{ command }}</li>
          }
        </ul>
      </section>

      <section class="av-card mt-3 p-4 d-flex flex-column gap-2" data-testid="settings-dedup">
        <h3 class="av-section-title m-0">{{ 'backoffice.settings.dedup.title' | translate }}</h3>
        <span class="cell-strong" data-testid="settings-dedup-ttl">
          {{ 'backoffice.settings.dedup.value' | translate: { seconds: settings.dedup_ttl } }}
        </span>
        <p class="av-meta m-0">{{ 'backoffice.settings.dedup.hint' | translate }}</p>
      </section>
    }
  }
  @default {
    <av-empty-state
      class="mt-3"
      icon="hourglass_empty"
      [title]="'backoffice.settings.loading.title' | translate"
      [message]="'backoffice.settings.loading.message' | translate"
    ></av-empty-state>
  }
}
